import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { useResizeObserver } from '@/hooks/useResizeObserver';
import { Input } from '@/lib/base/forms/input/input';
import type { SelectSize } from '@/lib/base/forms/select/select-shared';
import { TextArea } from '@/lib/base/forms/textarea/textarea';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { cx } from '@/utils/cx';

// 1-row floor (vs. TextAreaInput's 3-row MIN_ROWS): this field sits inline
// among single-line property rows, so it should read as a one-line field
// until focused — TextAreaInput's larger floor is a deliberate divergence
// for its standalone multi-line use case, not something to unify here.
const LINE_HEIGHT = 24;
const MAX_ROWS = 10;

// Blurred overflow cue. `text-ellipsis` is kept for engines that honour it on
// <textarea> (Firefox), but Chromium does not render text-overflow on
// textareas at all — the row just clips hard. So, mirroring
// lib/base/collapsible-content, we measure real overflow and fade the
// trailing edge with a CSS mask (glyphs fade to whatever surface is behind
// them — background- and theme-independent). Applied ONLY when the value
// actually overflows, so a normal value renders pixel-identical to <input>.
// The fade sits over the last 2rem of the content box; with a trailingAddon
// the textarea carries `pr-9` (2.25rem) so the fade is offset past it.
// `calc()` requires whitespace around the `-` operator, and Chromium drops the
// whole mask (silently losing the fade cue) if it is missing. In Tailwind
// arbitrary values underscores compile to spaces, so encode the spaces as
// `_-_` to emit `calc(100% - 2rem)` rather than the invalid `calc(100%-2rem)`.
const OVERFLOW_FADE_CLASS =
  '[mask-image:linear-gradient(to_right,black_calc(100%_-_2rem),transparent)]';
const OVERFLOW_FADE_WITH_ADDON_CLASS =
  '[mask-image:linear-gradient(to_right,black_calc(100%_-_4.25rem),transparent_calc(100%_-_2.25rem))]';

// Text properties are single-line: collapse any newline (and the whitespace
// hugging it) to a single space. Paste is normalized inline, but existing or
// imported values can already contain `\n`, so the controlled value and every
// edit are normalized before render to hold the single-line contract.
const normalizeSingleLine = (text: string): string =>
  text.replace(/\s*\n\s*/g, ' ');

export interface PropertyInputProps {
  propertyType: PropertyTypeSchema;
  value: string | number | object | null | undefined;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  placeholder?: string;
  /** Node rendered inside the input's trailing slot (e.g. a multi-value "+"). */
  trailingAddon?: React.ReactNode;
  /**
   * Open the options popover immediately on mount. Only meaningful for
   * enumerated pickers (SelectInput); free-text editors ignore it. Used by
   * table-cell editing so entering edit mode lands directly in the options.
   */
  autoOpenPicker?: boolean;
  /**
   * Notified when a picker-backed editor's popover opens or closes (date and
   * datetime inputs). Lets a host that auto-opens the picker tear its own edit
   * mode down when the popover is dismissed (Escape / click-away) without a
   * selection. Ignored by field types that have no popover.
   */
  onPickerOpenChange?: (isOpen: boolean) => void;
  /**
   * Field size on the shared library scale (`SelectSize` — the same scale
   * every `lib/base` form control uses). Defaults to 'sm'.
   */
  size?: SelectSize;
  /** Element for the text leaf. 'form' (default) = focus-expanding textarea editor;
   *  'card' = today's bare <input>, required where an HTMLInputElement focus ref /
   *  [&_input] chrome must keep working (table cell). Mirrors phoneDisplay/markdownDisplay. */
  textDisplay?: 'form' | 'card';
}

const TextPropertyFormInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  inputRef,
  placeholder,
  trailingAddon,
  size = 'sm',
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingCaretRef = useRef<number | null>(null);
  // Both written only from the focus handler / the pointer listeners below —
  // never during render.
  const pointerPressedRef = useRef(false);
  const blurDeferredRef = useRef(false);

  // Pressing a button elsewhere blurs this field on pointerdown. Collapsing the
  // expanded textarea at that instant shifts everything below it — including
  // the button under the pointer — before pointerup, and react-aria's usePress
  // cancels a press whose pointerup no longer lands inside the pressed element,
  // so the click is silently lost. A long deal name plus "Create deal"
  // reproduced this deterministically (nothing happened on the first click).
  // A blur that arrives while a pointer is pressed is therefore held until
  // that press ends; keyboard (Tab) and window blur collapse immediately.
  const handleFocusChange = useCallback((focused: boolean) => {
    if (focused) {
      blurDeferredRef.current = false;
      setIsFocused(true);
    } else if (pointerPressedRef.current) {
      blurDeferredRef.current = true;
    } else {
      setIsFocused(false);
    }
  }, []);

  useEffect(() => {
    if (!isFocused) {
      return undefined;
    }
    const onPointerDown = () => {
      pointerPressedRef.current = true;
    };
    // Shared by pointerup/pointercancel and window blur: resolve the pending
    // press and flush a deferred blur if one is waiting.
    const resolvePendingPress = () => {
      pointerPressedRef.current = false;
      if (blurDeferredRef.current) {
        blurDeferredRef.current = false;
        setIsFocused(false);
      }
    };
    // A window blur (OS focus loss — e.g. Alt+Tab, or the mouse button being
    // released outside the browser window) during a pending press must also
    // flush a deferred blur: pointerup/pointercancel are not guaranteed to
    // fire once the pointer leaves the browser, so without this the field
    // could stay stuck expanded until an unrelated later pointerup happens.
    const onWindowBlur = () => {
      resolvePendingPress();
    };
    // Capture phase: the flag must be set before the press's default action
    // (focus moving to the pressed element) fires this field's blur.
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('pointerup', resolvePendingPress, true);
    document.addEventListener('pointercancel', resolvePendingPress, true);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('pointerup', resolvePendingPress, true);
      document.removeEventListener('pointercancel', resolvePendingPress, true);
      window.removeEventListener('blur', onWindowBlur);
      pointerPressedRef.current = false;
      blurDeferredRef.current = false;
    };
  }, [isFocused]);

  // Normalize the incoming value once, so render, overflow measurement, and
  // caret math all operate on the same single-line text.
  const normalizedValue = normalizeSingleLine((value as string) || '');

  const handleChange = useCallback(
    (next: string) => {
      onChange(normalizeSingleLine(next));
    },
    [onChange]
  );

  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      if (inputRef) {
        (inputRef as React.RefObject<HTMLElement | null>).current = node;
      }
    },
    [inputRef]
  );

  const adjustHeight = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) {
      return;
    }
    if (!isFocused) {
      // Blurred state is always exactly one row (overflow-hidden +
      // whitespace-nowrap ellipsis clip content to a single line), so its
      // height never depends on content. Clear the inline height override so
      // the textarea falls back to its rows={1} natural height — which the
      // browser computes as line-height PLUS the shared TextArea's vertical
      // padding. Hard-coding LINE_HEIGHT here set a border-box height of one
      // line with no room for that padding, clipping the visible text. This
      // also keeps the fast path: it's a write, not the 'auto'-reset-then-
      // scrollHeight-read below that forces a synchronous layout, so a
      // bulk-populated multi-value list of blurred rows stays cheap.
      ta.style.height = '';
      return;
    }
    ta.style.height = 'auto';
    const max = MAX_ROWS * LINE_HEIGHT;
    ta.style.height =
      Math.min(max, Math.max(LINE_HEIGHT, ta.scrollHeight)) + 'px';
  }, [isFocused]);

  // Horizontal overflow only exists in the blurred (nowrap) state; while
  // focused the value wraps, so the cue is irrelevant and we skip the read.
  const checkOverflow = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) {
      return;
    }
    const overflow = !isFocused && ta.scrollWidth > ta.clientWidth;
    setIsOverflowing(prev => (prev === overflow ? prev : overflow));
  }, [isFocused]);

  useLayoutEffect(() => {
    adjustHeight();
    checkOverflow();
  }, [value, isFocused, adjustHeight, checkOverflow]);

  useResizeObserver({ ref: textareaRef, onResize: checkOverflow });

  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (ta && pendingCaretRef.current != null) {
      ta.setSelectionRange(pendingCaretRef.current, pendingCaretRef.current);
      pendingCaretRef.current = null;
    }
  }, [value]);

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      event.preventDefault();
      const pasted = normalizeSingleLine(event.clipboardData.getData('text'));
      const current = normalizedValue;
      const ta = textareaRef.current;
      const start =
        ta && ta.selectionStart != null ? ta.selectionStart : current.length;
      const end =
        ta && ta.selectionEnd != null ? ta.selectionEnd : current.length;
      const next = current.slice(0, start) + pasted + current.slice(end);
      pendingCaretRef.current = start + pasted.length;
      onChange(next);
    },
    [normalizedValue, onChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Scope the Enter guard to the textarea itself. This handler sits on the
      // wrapper to catch the textarea's bubbled keydown (base TextArea doesn't
      // forward onKeyDown), but an interactive trailingAddon (e.g. the
      // multi-value "+" button) also bubbles through here — preventing Enter
      // there would block the button's keyboard activation.
      if (
        event.target === textareaRef.current &&
        event.key === 'Enter' &&
        !event.nativeEvent.isComposing
      ) {
        event.preventDefault();
      }
    },
    []
  );

  return (
    <div
      className='relative w-full'
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
    >
      <TextArea
        ref={setRefs}
        value={normalizedValue}
        onChange={handleChange}
        onFocusChange={handleFocusChange}
        isDisabled={disabled}
        rows={1}
        resize='none'
        size={size}
        aria-label={propertyType.label}
        placeholder={placeholder}
        textareaClassName={cx(
          isFocused
            ? 'whitespace-normal break-words'
            : 'overflow-hidden whitespace-nowrap text-ellipsis',
          !isFocused &&
            isOverflowing &&
            (trailingAddon
              ? OVERFLOW_FADE_WITH_ADDON_CLASS
              : OVERFLOW_FADE_CLASS),
          trailingAddon && 'pr-9'
        )}
      />
      {trailingAddon && (
        <div className='absolute inset-y-0 right-1.5 flex items-center'>
          {trailingAddon}
        </div>
      )}
    </div>
  );
};

export const TextInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  inputRef,
  placeholder,
  trailingAddon,
  size = 'sm',
  textDisplay = 'form',
}) => {
  if (textDisplay === 'card') {
    return (
      <Input
        type='text'
        value={(value as string) || ''}
        onChange={onChange}
        isDisabled={disabled}
        size={size}
        aria-label={propertyType.label}
        ref={inputRef}
        placeholder={placeholder}
        trailingAddon={trailingAddon}
      />
    );
  }

  return (
    <TextPropertyFormInput
      propertyType={propertyType}
      value={value}
      onChange={onChange}
      disabled={disabled}
      inputRef={inputRef}
      placeholder={placeholder}
      trailingAddon={trailingAddon}
      size={size}
    />
  );
};
